import { useRequest } from "./use-request";
import {
  Form,
  Input,
  message,
  Modal,
  ModalProps,
  Radio,
  Select,
} from "@gw/web-basic-components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CompanyIdReq,
  CompanyIdRes,
  IDetail,
  RelorcreateTenantRes,
  ReltypesReq,
  ReltypesRes,
  TenantExistReq,
  TenantExistRes,
} from "./dto";
import { apis } from "./use-request";
import { debouncePromise, getDefaultTitle, phonePattern } from "./utils";
import { SelectInnerTag } from "./select-inner-tag";
import { CREATE_TENANT_TYPES } from "./constants";

interface IProps extends ModalProps {
  /**
   * 所属租户 id
   */
  belongTenantId?: string;

  /**
   * 公司 id
   */
  id?: string;

  /**
   * 新建成功的回调
   */
  callback?: (id: string) => void;

  /**
   * 关联方类型选项
   */
  relTypesOptions?: {
    label: string;
    value: number;
  }[];
}

/**
 * 新增/编辑 关联方公司弹窗
 */
const RelatedCompanyModal = ({
  id,
  title,
  open,
  onOk,
  callback,
  centered,
  okText,
  loading,
  cancelText,
  belongTenantId,
  relTypesOptions: customRelTypesOptions = [],
  ...rest
}: IProps) => {
  const [{ loading: fetchCreateOrUpdateLoading }, fetchCreateOrUpdate] =
    useRequest<Partial<RelorcreateTenantRes>, Partial<RelorcreateTenantRes>>(
      apis.relatedCompany.createOrUpdateTenant
    );

  const [, fetchIsExistTenant] = useRequest<TenantExistReq, TenantExistRes>(
    apis.relatedCompany.isExistTenant
  );

  const [{ data: relTypes, loading: fetchRelTypesLoading }, fetchRelTypes] =
    useRequest<ReltypesReq, ReltypesRes>(apis.relatedCompany.getRelTypes);

  const [{ loading: fetchDetailLoading }, fetchDetail] = useRequest<
    CompanyIdReq,
    CompanyIdRes
  >(apis.relatedCompany.getCompanyDetail);

  const debouncedFetchIsExistTenant = useCallback(
    debouncePromise(fetchIsExistTenant, 300),
    []
  );

  const popContainRef = useRef<HTMLDivElement>(null);

  const [form] = Form.useForm<IDetail>();

  const [isExistTenant, setIsExistTenant] = useState<boolean>(false);

  const [detail, setDetail] = useState<Partial<IDetail> | null>(null);

  const isEdit = typeof id === "string";

  const isCustomRelTypesOptions =
    Array.isArray(customRelTypesOptions) && customRelTypesOptions.length > 0;

  const relTypesOptions = isCustomRelTypesOptions
    ? customRelTypesOptions
    : relTypes?.dataList?.map((item) => ({
        label: item.name,
        value: item.relType,
      })) ?? [];

  const handleSubmit = async (values: Partial<IDetail>) => {
    await fetchCreateOrUpdate({
      ...values,
      ...(isEdit ? { id } : { belongTenantId }),
    }).then((res) => {
      message.success(isEdit ? "编辑成功" : "新建成功");

      if (callback) {
        callback(res?.id);
      }
    });
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setDetail(null);
    } else {
      if (isEdit) {
        fetchDetail({ id }).then((res) => {
          const formatedValue: Partial<IDetail> = {
            ...res,
            companyName: res?.name,
            isCreateTenant: Boolean(res?.relTenantId),
          };

          setDetail(formatedValue);

          form.setFieldsValue(formatedValue);
        });
      }

      if (!isCustomRelTypesOptions) {
        fetchRelTypes({ category: 1 });
      }
    }
  }, [open, isEdit, id, isCustomRelTypesOptions]);

  return (
    <Modal
      {...rest}
      open={open}
      centered={centered ?? true}
      loading={loading ?? (fetchCreateOrUpdateLoading || fetchDetailLoading)}
      title={getDefaultTitle(isEdit, title)}
      onOk={async (e) => {
        try {
          const values = await form.validateFields();

          await handleSubmit(values);

          onOk?.(e);
        } catch (error) {
          console.log(error);
        }
      }}
      okText={okText ?? "确定"}
      cancelText={cancelText ?? "取消"}
    >
      <div ref={popContainRef} className={"wbuc-add-related-company-contain"}>
        <Form
          form={form}
          initialValues={{
            relTypes:
              relTypesOptions.length === 1 ? [relTypesOptions[0].value] : [],
          }}
        >
          <div className={"wbuc-add-related-company-grid"}>
            <Form.Item
              label="关联方名称"
              name={"companyName"}
              rules={[{ required: true, message: "请输入关联方名称" }]}
            >
              <Input
                disabled={isEdit}
                size="large"
                placeholder="请输入关联方名称"
                maxLength={100}
                onChange={(event) => {
                  if (isEdit) return;

                  debouncedFetchIsExistTenant({
                    name: event.target.value,
                  }).then((res) => {
                    setIsExistTenant(res);

                    form.setFieldsValue({
                      isCreateTenant: res,
                    });
                  });
                }}
              />
            </Form.Item>

            <Form.Item
              label="关联类型"
              name={"relTypes"}
              rules={[{ required: true, message: "请选择关联类型" }]}
              shouldUpdate
            >
              <Select
                loading={fetchRelTypesLoading}
                size="large"
                mode="multiple"
                options={relTypesOptions}
                placeholder="选择关联类型"
                getPopupContainer={() => popContainRef.current}
                onChange={(value) => {
                  // 当关联方类型为这些类型时，isCreateTenant可编辑为是
                  const isIncludeRelType = CREATE_TENANT_TYPES.some((item) =>
                    value?.includes(item)
                  );

                  if (!isIncludeRelType && !isExistTenant) {
                    form.setFieldsValue({
                      isCreateTenant: false,
                    });
                  }
                }}
                // 编辑时仅支持增加关联类型
                tagRender={({ closable, value, ...restProps }) => (
                  <SelectInnerTag
                    closable={!detail?.relTypes?.includes(value)}
                    {...restProps}
                  />
                )}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                // 当关联方类型为这些类型时，isCreateTenant可编辑为是
                const isIncludeRelType = CREATE_TENANT_TYPES.some((item) =>
                  getFieldValue("relTypes")?.includes(item)
                );

                return (
                  <Form.Item
                    label="是否开启WE平台账号"
                    name={"isCreateTenant"}
                    rules={[
                      { required: true, message: "请选择是否开启WE平台账号" },
                    ]}
                  >
                    <Radio.Group
                      disabled={
                        isExistTenant ||
                        !isIncludeRelType ||
                        // 编辑时仅能从否——>是
                        (isEdit && detail?.isCreateTenant === true)
                      }
                    >
                      <Radio value={true}>是</Radio>
                      <Radio value={false}>否</Radio>
                    </Radio.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              label="纳税人识别号"
              name={"taxNumber"}
              rules={[
                {
                  pattern: /^[0-9A-Z]*$/,
                  message: "请输入正确格式的纳税人识别号",
                },
              ]}
            >
              <Input placeholder="请输入纳税人识别号" maxLength={25} />
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const isCreateTenant = getFieldValue("isCreateTenant");

                if (
                  !isCreateTenant ||
                  isExistTenant ||
                  (isEdit && detail?.isCreateTenant)
                ) {
                  return null;
                }

                return (
                  <>
                    <Form.Item
                      label="管理员姓名"
                      name={"adminName"}
                      validateTrigger="onChange"
                      rules={[
                        {
                          required: true,
                          pattern: /^[\u4e00-\u9fa5a-zA-Z0-9]+$/,
                          message: "请输入正确格式的姓名",
                        },
                      ]}
                    >
                      <Input placeholder="请输入管理员姓名" maxLength={120} />
                    </Form.Item>

                    <Form.Item
                      label="管理员手机号码"
                      name={"adminPhone"}
                      validateTrigger="onChange"
                      rules={[
                        {
                          required: true,
                          validator: phonePattern,
                          message: "请输入正确格式的手机号码",
                        },
                      ]}
                    >
                      <Input
                        placeholder="请输入管理员手机号码"
                        maxLength={11}
                      />
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default RelatedCompanyModal;
